//npm packages
import React, { useEffect, useCallback, memo, useState } from 'react';
import { connect, shallowEqual, useSelector } from 'react-redux';
import { Field, reduxForm, change, formValues } from 'redux-form';
import { Row, Button } from 'reactstrap';
import { faTrash, faFile } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';

//redux actions
import { equipmentForm, EQUIPMENTFROM } from '../../redux/actions';

//elements starts with capital letter;
import { InputField, SelectField, FormBase, Validator } from '../../components';

//helper files
import consts from '../../consts';
import strings from '../../globalization';
import utility from '../../utility';
import dayjs from 'dayjs';
import BasicRowColGrid from '../../utility/BasicRowColGrid';

const equipmentsArray = [
  '612743ffc148926e3c177ac5',
  '612743ffc148926e3c177ac4',
  '612743ffc148926e3c177ac3'
];
const EquipmentForm = memo(props => {
  const {
      dispatch,
      comboType = {},
      initialValues = {},
      formValues = {},
      CustomError,
      err
    } = props,
    { params } = props.match;
  const [state, setState] = useState({
    disableAccesLocation: false,
    isDateFormatEnable: false,
    showAdvanceButton: false
  });
  const [modelOptions, setModelOptions] = useState([]);
  const [equipmentFamilyOptions, setEquipmentFamilyOptions] = useState([]);
  const [equipmentId, setEquipmentId] = useState('');

  useEffect(() => {
    dispatch(
      equipmentForm({
        action: 'load',
        _id: params.id.toLowerCase === 'new' ? 'new' : params.id,
        comboType: [
          'EquipmentType',
          'ConnectivityType',
          'EquipmentState',
          'EquipmentFamily',
          'EquipmentModel'
        ]
      })
    );
  }, []);

  useEffect(() => {
    if (CustomError && err) {
      Swal.fire({
        title: err,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No'
      }).then(result => {
        if (result.value) {
          dispatch(
            equipmentForm({
              action: 'otherAction',
              otherAction: 'UpdateSerialNumber',
              SerialNumber: initialValues.SerialNumber,
              LicenseId: params.licenseId
            })
          );
        }
      });
    }
  }, [CustomError, err]);

  const onSubmit = useCallback(values => {
    const { params } = props.match;
    const comboFields = [
      { name: 'EquipmentTypeId', isArray: false },
      { name: 'EquipmentFamilyId', isArray: false },
      { name: 'EquipmentState', isArray: false },
      { name: 'ModelId', isArray: false }
    ];
    const data = utility.processDataBeforeSave(values, comboFields);
    Object.assign(data, { tableName: 'Equipment' });
    if (params.id.toLowerCase() === 'new') {
      data.LicenseId = params.licenseId;
    }
    if (equipmentId.includes(equipmentsArray)) {
      data.ModelId = null;
      data.SerialNumber = '';
      data.EquipmentFamilyId = null;
      data.DateDeactivated = '';
      data.ModelName = '';
    }
    data.ExpirationDate =
      data.ExpirationDate ||
      dayjs()
        .add(10, 'years')
        .endOf('month')
        .add(1, 'day');

    dispatch(
      equipmentForm({
        action: params.id.toLowerCase() === 'new' ? 'add' : 'update',
        ...data
      })
    );
  }, []);

  const equipmentOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'Name' },
      comboType.EquipmentType
    ) || [];

  const transferPDFOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.TransferPDF
    ) || [];

  const equipmentStateOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.EquipmentState
    ) || [];

  const dateFormatOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.EquipmentDateFormat
    ) || [];

  useEffect(() => {
    if (
      Object.values(initialValues || {}).length &&
      comboType.EquipmentModel &&
      !Object.values(formValues || {}).length
    ) {
      onEquipmentTypeChange({ value: initialValues.EquipmentTypeId });
      onEquipmentFamilyChangeChange({ value: initialValues.EquipmentFamilyId });
      onModelChange({
        value: initialValues.ModelId,
        initialValues: initialValues
      });
    }
  }, [initialValues, formValues, comboType.EquipmentModel]);

  const onEquipmentTypeChange = ({ value }) => {
    let equipmentFamilyOptions = comboType.EquipmentFamily.filter(
      option => option.ScopeId === value
    );
    equipmentFamilyOptions =
      utility.remoteComboLoader(
        { valueProp: '_id', displayValue: 'Name' },
        equipmentFamilyOptions
      ) || [];
    setEquipmentFamilyOptions(equipmentFamilyOptions);
    setEquipmentId(value);
  };
  const onEquipmentFamilyChangeChange = ({ value }) => {
    let modelOptions = comboType.EquipmentModel.filter(
      option => option.EquipmentFamilyId === value
    );
    modelOptions = modelOptions.filter(
      f => f.IsDeleted === false && f.IsActive === true
    );
    modelOptions =
      utility.remoteComboLoader(
        { valueProp: '_id', displayValue: 'ModelName' },
        modelOptions
      ) || [];
    setModelOptions(modelOptions);
  };
  const onModelChange = ({ value, initialValues }) => {
    const disableAccesLocationArray = [
      consts.ConnectivityType.Proconnect,
      consts.ConnectivityType.CATSync
    ];
    const enableDateFormat = [
      consts.ConnectivityType.Proconnect,
      consts.ConnectivityType.CSVConvert
    ];
    const [modelCombo] = comboType.EquipmentModel.filter(
      option => option._id === value
    );
    const isAccessLocationDisable = disableAccesLocationArray.includes(
      modelCombo?.ConnectivityType
    );
    const isDateFormatEnable = enableDateFormat.includes(
      modelCombo?.ConnectivityType
    );
    if (isAccessLocationDisable) {
      props.change('AccessLocation', '');
    }
    if (!isDateFormatEnable) {
      props.change('DateFormat', '');
    }
    setState({
      disableAccesLocation: isAccessLocationDisable,
      isDateFormatEnable,
      showAdvanceButton: Object.values(initialValues || {}).length != 0
    });
  };
  const { disableAccesLocation, isDateFormatEnable, showAdvanceButton } = state;
  const onEquipmentDelete = () => {
    const { params } = props.match;
    Swal.fire({
      title: strings.DeleteWarning,
      text: strings.DeleteText,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: strings.DeleteConfirmButton
    }).then(result => {
      if (result.value) {
        dispatch(
          equipmentForm({
            action: 'delete',
            _id: params.id
          })
        );
      }
    });
  };
  const getCertificate = () => {
    const { params } = props.match;
    if (!initialValues.ClientCommonName) {
      Swal.fire(strings.info, strings.CertificateInformation, 'info');
    } else {
      const url = new URL(`${consts.hostURL}/download`);
      url.searchParams.append('LicenseId', params.licenseId);
      url.searchParams.append('EquipmentId', params.id);
      url.searchParams.append(
        'ClientCommonName',
        initialValues.ClientCommonName
      );
      url.toString();
      window.open(url, '_blank');
    }
  };
  const advanceButtons = (
    <Button
      title={strings.Delete}
      size="sm"
      color="danger"
      onClick={() => onEquipmentDelete()}
    >
      <FontAwesomeIcon icon={faTrash} /> {strings.Delete}
    </Button>
  );
  const loginData = useSelector(state => {
    const { userDetail } = state.loginData.data || {};
    return userDetail;
  }, shallowEqual);
  const shouldNotDownload =
    loginData && consts.UserRoleId.SuperAdmin.includes(loginData.UserRole);

  const FormButtons = (
    <Button
      title={strings.GenerateCertificate}
      type="button"
      disabled={
        shouldNotDownload ||
        initialValues.EquipmentStateName?.includes('DeActivated')
      }
      color="info"
      onClick={() => getCertificate()}
    >
      {' '}
      <FontAwesomeIcon icon={faFile} /> {strings.GenerateCertificate}
    </Button>
  );
  return (
    <FormBase
      {...props}
      onSubmit={onSubmit}
      isDark={props.isDark}
      title={strings.Equipment}
      subTitle={(initialValues && initialValues.SerialNumber) || strings.New}
      actionInfo={{ action: equipmentForm, actionName: EQUIPMENTFROM }}
      showAdvanceButton={showAdvanceButton}
      FormButtons={!initialValues.ClientCommonName ? '' : FormButtons}
    >
      <Row sm="12" md="12" lg="12">
        <BasicRowColGrid>
          {strings.EquipmentType}
          <Field
            name="EquipmentTypeId"
            type="select"
            component={SelectField}
            placeholder={strings.EquipmentType}
            options={equipmentOptions}
            validate={Validator.required}
            onChange={value => onEquipmentTypeChange(value)}
          />
        </BasicRowColGrid>
        {!equipmentsArray.includes(equipmentId) && (
          <BasicRowColGrid>
            {strings.EquipmentFamily}
            <Field
              name="EquipmentFamilyId"
              type="select"
              component={SelectField}
              placeholder={strings.EquipmentFamily}
              options={equipmentFamilyOptions}
              validate={Validator.required}
              onChange={value => onEquipmentFamilyChangeChange(value)}
            />
          </BasicRowColGrid>
        )}
        {!equipmentsArray.includes(equipmentId) && (
          <BasicRowColGrid>
            {strings.EquipmentModel}
            <Field
              name="ModelId"
              type="select"
              component={SelectField}
              placeholder={strings.EquipmentModel}
              options={modelOptions}
              validate={Validator.required}
              onChange={newValue => onModelChange(newValue)}
            />
          </BasicRowColGrid>
        )}
        {!equipmentsArray.includes(equipmentId) && (
          <BasicRowColGrid>
            {strings.SerialNumber}
            <Field
              name="SerialNumber"
              type="text"
              maxLength={100}
              component={InputField}
              placeholder={strings.SerialNumber}
              validate={[Validator.required]}
            />
          </BasicRowColGrid>
        )}
        {!equipmentsArray.includes(equipmentId) && (
          <BasicRowColGrid>
            {strings.EquipmentState}
            <Field
              name="EquipmentState"
              type="select"
              component={SelectField}
              placeholder={strings.EquipmentState}
              options={equipmentStateOptions}
              defaultValue={{
                value: '5f882239faeb084c680a2f53',
                label: 'Active',
                isDisabled: false
              }}
            />
          </BasicRowColGrid>
        )}
        {!equipmentsArray.includes(equipmentId) && (
          <BasicRowColGrid>
            {strings.DateDeactivated}
            <Field
              name="DateDeactivated"
              type="date"
              component={InputField}
              placeholder={strings.DateDeactivated}
              min={
                dayjs()
                  .add(1, 'day')
                  .toISOString()
                  .split('T')[0]
              }
            />
          </BasicRowColGrid>
        )}
        <BasicRowColGrid>
          {strings.Notes}
          <Field
            name="Notes"
            type="textarea"
            component={InputField}
            placeholder={strings.Notes}
            rows="5"
            className="wt-resize"
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.ClientCommonName}
          <Field
            name="ClientCommonName"
            type="text"
            component={InputField}
            placeholder={strings.ClientCommonName}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.ExpirationDate}
          <Field
            name="ExpirationDate"
            type="date"
            component={InputField}
            placeholder={strings.ExpirationDate}
            min={
              dayjs()
                .add(1, 'day')
                .toISOString()
                .split('T')[0]
            }
          />
        </BasicRowColGrid>
      </Row>
    </FormBase>
  );
});

const EquipmentReduxForm = reduxForm({
  form: 'equipmentForm',
  enableReinitialize: false,
  keepValues: true
})(EquipmentForm);

const mapDispatchToProps = dispatch => ({
  change: (field, value) => dispatch(change('equipmentForm', field, value))
});

const mapStateToProps = state => {
  const { equipmentForm } = state.form;
  const {
    data,
    success,
    info,
    CustomError,
    err
  } = state.equipmentFormData.data;
  const { isDark } = state.settingData;
  return {
    initialValues: data,
    comboType: state.comboTypes,
    isDark: isDark,
    formValues: equipmentForm ? equipmentForm.values : null,
    shouldGoBack:
      success &&
      (info === 'RecordSavedSuccessfully' ||
        info === 'RecordUpdatedSuccessfully' ||
        info === 'DeleteSuccess'),
    loginUserData: state.loginData.data,
    CustomError,
    err
  };
};

const EquipmentFormModule = connect(
  mapStateToProps,
  mapDispatchToProps
)(EquipmentReduxForm);

export default props => {
  return <EquipmentFormModule {...props} />;
};
